import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  api,
  ApiError,
  type BooksType,
  type Company,
  type CompanySettings,
  type NewSettings,
  type TaxationForm,
  type VatStatus,
  type ZusScheme,
  personTaxed,
  ownersInsured,
} from '../api';
import type { FiscalYear } from '../api';
import type { Dictionary, Locale } from '../i18n';
import { date as written } from '../format';
import * as Icon from '../components/Icons';

/** Pierwszy dzień następnego miesiąca — najczęstszy moment zmiany formy opodatkowania. */
function firstDayOfNextMonth(): string {
  const now = new Date();
  const next = new Date(Date.UTC(now.getFullYear(), now.getMonth() + 1, 1));
  return next.toISOString().slice(0, 10);
}

function emptySettings(company: Company): NewSettings {
  // Jednostka w CIT (spółki kapitałowe, organizacje) a osoby fizyczne i ich spółki (PIT)
  const spolka = !personTaxed(company.legalForm);
  return {
    validFrom: firstDayOfNextMonth(),
    taxationForm: spolka ? 'CIT' : 'SKALA',
    booksType: spolka ? 'FULL' : 'KPIR',
    vatStatus: 'ACTIVE_MONTHLY',
    vatExemptionBasis: null,
    zusScheme: ownersInsured(company.legalForm) ? 'PELNY' : null,
    cashMethod: false,
    smallTaxpayer: false,
    sicknessInsurance: true,
    incomeTaxPeriod: 'MONTHLY',
  };
}

/**
 * Forma opodatkowania wyznacza rodzaj ksiąg: przy ryczałcie prowadzi się ewidencję przychodów,
 * przy skali i liniowym — KPiR albo pełne księgi. Zmieniamy oba naraz, żeby formularz nie
 * wysłał połączenia, które i tak odrzuci serwer.
 */
function withTaxationForm(form: NewSettings, taxationForm: TaxationForm): NewSettings {
  const booksType: BooksType =
    taxationForm === 'RYCZALT' ? 'RYCZALT'
    : taxationForm === 'CIT' ? 'FULL'
    : form.booksType === 'RYCZALT' ? 'KPIR'
    : form.booksType;
  return { ...form, taxationForm, booksType };
}

/**
 * Do kiedy obowiązywał wpis: dzień przed początkiem następnego.
 *
 * Bez tej kolumny nie widać, czy wpis jest bieżący, czy historyczny — a to pierwsza rzecz,
 * o którą pyta się patrząc na listę okresów obowiązywania. Historia przychodzi od najnowszego.
 */
function validTo(history: CompanySettings[], index: number): string | null {
  const newer = history[index - 1];
  if (!newer) return null;
  const day = new Date(`${newer.validFrom}T00:00:00`);
  day.setDate(day.getDate() - 1);
  return day.toISOString().slice(0, 10);
}

export function CompanySettingsPanel({
  company,
  t,
  locale,
  onChanged,
}: {
  company: Company;
  t: Dictionary;
  locale: Locale;
  /**
   * Zmiana ustawień zmienia to, co firma w ogóle ma w nawigacji: przejście na pełne księgi
   * odsłania plan kont. Bez odświeżenia listy firm człowiek ustawia pełną księgowość i nic
   * się nie dzieje aż do przeładowania strony — czyli wygląda to na usterkę, a nie na zapis.
   */
  onChanged?: () => void;
}) {
  const [history, setHistory] = useState<CompanySettings[] | null>(null);
  const [form, setForm] = useState<NewSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Identyfikator poprawianego wpisu; null znaczy „dopisujemy nowy". */
  const [editing, setEditing] = useState<string | null>(null);
  const [years, setYears] = useState<FiscalYear[]>([]);

  const load = useCallback(async () => {
    try {
      setHistory(await api.settings(company.id));
      // Lata obrotowe czytamy po to, żeby zauważyć rok, którego żaden wpis nie obejmuje
      setYears(await api.fiscalYears(company.id));
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    }
  }, [company.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const spolka = !personTaxed(company.legalForm);
  const insured = ownersInsured(company.legalForm);
  /** Wpis, który dopiero zacznie obowiązywać — dziś jeszcze nic z niego nie wynika. */
  const future = (entry: CompanySettings) => entry.validFrom > new Date().toISOString().slice(0, 10);

  /*
   * Rok obrotowy zaczynający się przed pierwszym wpisem ustawień.
   *
   * Firma wchodzi do Firmeto w trakcie roku i wpisuje ustawienia „od dziś", a rok obrotowy zaczął
   * się wcześniej — czasem o rok, bo pierwszy rok wolno wydłużyć. Wtedy dla początku roku nie
   * wiadomo, jak firma się rozlicza, a dowiaduje się o tym dopiero na planie kont, w komunikacie
   * o dacie, której nigdzie nie wpisywała. Mówimy o tym tutaj, bo tutaj się to naprawia.
   */
  const luka = useMemo(() => {
    const wpisy = [...(history ?? [])].sort((a, b) => a.validFrom.localeCompare(b.validFrom));
    const pierwszy = wpisy[0];
    if (!pierwszy) return null;
    const rok = years
      .filter((year) => year.startsOn < pierwszy.validFrom)
      .sort((a, b) => a.startsOn.localeCompare(b.startsOn))[0];
    return rok ? { rok, pierwszy } : null;
  }, [history, years]);

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!form) return;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...form,
        // Podstawę zwolnienia wysyłamy tylko wtedy, gdy firma jest zwolniona z VAT
        vatExemptionBasis: form.vatStatus === 'EXEMPT' ? form.vatExemptionBasis?.trim() || null : null,
      };
      if (editing) await api.changeSettings(company.id, editing, payload);
      else await api.addSettings(company.id, payload);
      setForm(null);
      setEditing(null);
      await load();
      onChanged?.();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setSaving(false);
    }
  }

  /** Eksport zapisujemy prosto na dysk — to plik do przeniesienia, nie treść do oglądania. */
  async function exportData() {
    setSaving(true);
    setError(null);
    try {
      const file = await api.exportCompany(company.id);
      const url = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.download = `firmeto-${company.nip}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="hd">
        <div>
          <h3>{t.settings.title}</h3>
          <p>{t.settings.lead}</p>
        </div>
        <div className="sp">
          <button type="button" className="btn sec" disabled={saving} onClick={() => void exportData()}>
            <Icon.Download />
            {t.settings.exportData}
          </button>
        </div>
      </div>

      {error && (
        <div className="alert a-bad">
          <Icon.Error />
          <div>
            <b>{error}</b>
          </div>
        </div>
      )}

      {/*
        * Ostrzeżenie stoi nad okresami, bo dotyczy ich wprost, i niesie gotową datę: człowiek
        * nie ma zgadywać, od kiedy zaczyna się jego rok obrotowy — my to wiemy.
        */}
      {luka && !form && (
        <div className="alert a-warn">
          <Icon.Warn />
          <div>
            <b>{t.settings.yearNotCovered(written(luka.rok.startsOn, locale), written(luka.rok.endsOn, locale))}</b>
            <p>{t.settings.yearNotCoveredHint(written(luka.pierwszy.validFrom, locale), written(luka.rok.startsOn, locale))}</p>
          </div>
          <button
            type="button"
            className="btn"
            onClick={() => {
              setEditing(luka.pierwszy.id);
              setForm({
                validFrom: luka.rok.startsOn,
                taxationForm: luka.pierwszy.taxationForm,
                booksType: luka.pierwszy.booksType,
                vatStatus: luka.pierwszy.vatStatus,
                vatExemptionBasis: luka.pierwszy.vatExemptionBasis,
                zusScheme: luka.pierwszy.zusScheme,
                cashMethod: luka.pierwszy.cashMethod,
                smallTaxpayer: luka.pierwszy.smallTaxpayer,
                sicknessInsurance: luka.pierwszy.sicknessInsurance,
                incomeTaxPeriod: luka.pierwszy.incomeTaxPeriod,
              });
            }}
          >
            {t.settings.coverFromYearStart(written(luka.rok.startsOn, locale))}
          </button>
        </div>
      )}

      <div className="panel">
        <header>
          <h4>{t.settings.periods}</h4>
          {!form && (
            <div className="sp">
              <button type="button" className="btn sec sm" onClick={() => { setEditing(null); setForm(emptySettings(company)); }}>
                <Icon.Plus />
                {t.settings.add}
              </button>
            </div>
          )}
        </header>

        {history?.length === 0 && !form && (
          <div className="alert a-warn">
            <Icon.Warn />
            <div>
              <b>{t.settings.none}</b>
            </div>
          </div>
        )}

        {history && history.length > 0 && (
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th>{t.settings.from}</th>
                  <th>{t.settings.to}</th>
                  <th>{t.settings.taxationForm}</th>
                  <th>{t.settings.booksType}</th>
                  <th>{t.settings.vatStatus}</th>
                  <th>{t.settings.zusScheme}</th>
                  <th>{t.costs.status}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {history.map((entry, index) => {
                  const until = validTo(history, index);
                  return (
                    <tr key={entry.id}>
                      <td className="doc">{written(entry.validFrom, locale)}</td>
                      <td className={until ? 'doc' : 'muted'}>
                        {until ? written(until, locale) : t.settings.openEnded}
                      </td>
                      <td>
                        {t.settings.forms[entry.taxationForm.toLowerCase() as keyof Dictionary['settings']['forms']]}
                      </td>
                      <td>
                        {t.settings.books[entry.booksType.toLowerCase() as keyof Dictionary['settings']['books']]}
                      </td>
                      <td>{t.settings.vat[entry.vatStatus.toLowerCase() as keyof Dictionary['settings']['vat']]}</td>
                      <td>
                        {entry.zusScheme
                          ? t.settings.zus[entry.zusScheme.toLowerCase() as keyof Dictionary['settings']['zus']]
                          : '—'}
                      </td>
                      <td>
                        {/*
                          * Trzy stany, nie dwa. Wpis zaczynający się jutro nie „obowiązuje" — a tak
                          * właśnie był podpisany i przez to firma, która ustawiła pełne księgi od
                          * przyszłego miesiąca, nie rozumiała, czemu nic się nie zmieniło.
                          */}
                        <span className={until ? 'chip st-draft' : future(entry) ? 'chip st-warn' : 'chip st-ok'}>
                          <i />
                          {until ? t.settings.past : future(entry) ? t.settings.future : t.settings.inForce}
                        </span>
                      </td>
                      <td className="act">
                        <div className="row">
                          <button
                            type="button"
                            className="btn ghost sm"
                            onClick={() => {
                              setEditing(entry.id);
                              setForm({
                                validFrom: entry.validFrom,
                                taxationForm: entry.taxationForm,
                                booksType: entry.booksType,
                                vatStatus: entry.vatStatus,
                                vatExemptionBasis: entry.vatExemptionBasis,
                                zusScheme: entry.zusScheme,
                                cashMethod: entry.cashMethod,
                                smallTaxpayer: entry.smallTaxpayer,
                                sicknessInsurance: entry.sicknessInsurance,
                                incomeTaxPeriod: entry.incomeTaxPeriod,
                              });
                            }}
                          >
                            {t.settings.edit}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {form && editing && (
          <div className="alert a-warn">
            <Icon.Warn />
            <div>
              <b>{t.settings.editWarning}</b>
              <p>{t.settings.editWarningHint}</p>
            </div>
          </div>
        )}

        {form && (
          <form onSubmit={save}>
            <p className="hint">{t.settings.monthHint}</p>
            <div className="fields">
              <div className="field">
                <label htmlFor="sFrom">{t.settings.from}</label>
                <input
                  id="sFrom"
                  className="inp"
                  type="date"
                  value={form.validFrom}
                  onChange={(event) => setForm({ ...form, validFrom: event.target.value })}
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="sForm">{t.settings.taxationForm}</label>
                <select
                  id="sForm"
                  className="inp"
                  value={form.taxationForm}
                  onChange={(event) => setForm(withTaxationForm(form, event.target.value as TaxationForm))}
                >
                  {(spolka ? (['CIT'] as const) : (['SKALA', 'LINIOWY', 'RYCZALT'] as const)).map((value) => (
                    <option key={value} value={value}>
                      {t.settings.forms[value.toLowerCase() as keyof Dictionary['settings']['forms']]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="sBooks">{t.settings.booksType}</label>
                <select
                  id="sBooks"
                  className="inp"
                  value={form.booksType}
                  onChange={(event) => setForm({ ...form, booksType: event.target.value as BooksType })}
                >
                  {(spolka
                    ? (['FULL'] as const)
                    : form.taxationForm === 'RYCZALT'
                      ? (['RYCZALT'] as const)
                      : (['KPIR', 'FULL'] as const)
                  ).map((value) => (
                    <option key={value} value={value}>
                      {t.settings.books[value.toLowerCase() as keyof Dictionary['settings']['books']]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="sVat">{t.settings.vatStatus}</label>
                <select
                  id="sVat"
                  className="inp"
                  value={form.vatStatus}
                  onChange={(event) => setForm({ ...form, vatStatus: event.target.value as VatStatus })}
                >
                  {(['ACTIVE_MONTHLY', 'ACTIVE_QUARTERLY', 'EXEMPT'] as const).map((value) => (
                    <option key={value} value={value}>
                      {t.settings.vat[value.toLowerCase() as keyof Dictionary['settings']['vat']]}
                    </option>
                  ))}
                </select>
              </div>
              {form.vatStatus === 'EXEMPT' && (
                <div className="field">
                  <label htmlFor="sBasis">{t.settings.vatExemptionBasis}</label>
                  <input
                    id="sBasis"
                    className="inp"
                    value={form.vatExemptionBasis ?? ''}
                    onChange={(event) => setForm({ ...form, vatExemptionBasis: event.target.value })}
                    placeholder="art. 113 ust. 1 ustawy o VAT"
                    required
                  />
                </div>
              )}
              {insured && (
                <div className="field">
                  <label htmlFor="sZus">{t.settings.zusScheme}</label>
                  <select
                    id="sZus"
                    className="inp"
                    value={form.zusScheme ?? 'PELNY'}
                    onChange={(event) => setForm({ ...form, zusScheme: event.target.value as ZusScheme })}
                  >
                    {(['ULGA_NA_START', 'PREFERENCYJNY', 'MALY_ZUS_PLUS', 'PELNY', 'BRAK'] as const).map((value) => (
                      <option key={value} value={value}>
                        {t.settings.zus[value.toLowerCase() as keyof Dictionary['settings']['zus']]}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="field">
                <label className="check" htmlFor="sCash">
                  <input
                    id="sCash"
                    type="checkbox"
                    checked={form.cashMethod}
                    onChange={(event) => setForm({ ...form, cashMethod: event.target.checked })}
                  />
                  {t.settings.cashMethod}
                </label>
                <label className="check" htmlFor="sSickness">
                  <input
                    id="sSickness"
                    type="checkbox"
                    checked={form.sicknessInsurance}
                    onChange={(event) => setForm({ ...form, sicknessInsurance: event.target.checked })}
                  />
                  {t.settings.sicknessInsurance}
                </label>
                <label className="check" htmlFor="sSmall">
                  <input
                    id="sSmall"
                    type="checkbox"
                    checked={form.smallTaxpayer}
                    onChange={(event) => setForm({ ...form, smallTaxpayer: event.target.checked })}
                  />
                  {t.settings.smallTaxpayer}
                </label>
                {/*
                  Okres zaliczek stoi obok „małego podatnika", bo to ta sama decyzja: kwartalne
                  przysługują właśnie jemu i podatnikowi rozpoczynającemu działalność. Warunku nie
                  sprawdzamy — zależy od przychodu poprzedniego roku — więc mówimy o nim w podpowiedzi.
                */}
                <div className="field">
                  <label htmlFor="sAdvance">{t.settings.incomeTaxPeriod}</label>
                  <select
                    id="sAdvance"
                    className="inp"
                    value={form.incomeTaxPeriod}
                    onChange={(event) =>
                      setForm({ ...form, incomeTaxPeriod: event.target.value as NewSettings['incomeTaxPeriod'] })
                    }
                  >
                    <option value="MONTHLY">{t.settings.advancePeriods.MONTHLY}</option>
                    <option value="QUARTERLY">{t.settings.advancePeriods.QUARTERLY}</option>
                  </select>
                  <p className="hint">{t.settings.incomeTaxPeriodHint}</p>
                </div>
              </div>
            </div>
            <div className="bar2">
              <button type="submit" className="btn" disabled={saving}>
                {saving ? t.companies.saving : t.settings.save}
              </button>
              <div className="sp">
                <button type="button" className="btn ghost" onClick={() => { setForm(null); setEditing(null); }} disabled={saving}>
                  {t.companies.cancel}
                </button>
              </div>
            </div>
          </form>
        )}
        <p className="note">{t.settings.exportHint}</p>
      </div>
    </>
  );
}
